import React from 'react';
import { IBlockData } from '@core/typings';
import { BasicType, AdvancedType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { Wrapper } from '../Wrapper';
import { merge } from 'lodash';

import { generaMjmlMetaData } from '@core/utils/generaMjmlMetaData';
import { BlockRenderer } from '@core/components/BlockRenderer';
import {
	getAdapterAttributesString,
	getChildIdx,
	getPageIdx,
	resolvePreviewColor,
} from '@core/utils';
import { t } from '@core/utils/I18nManager';

// List of Google Font names (must match the font list in the editor)
const GOOGLE_FONTS = [
	'Roboto',
	'Open Sans',
	'Lato',
	'Montserrat',
	'Poppins',
	'Raleway',
	'Oswald',
	'PT Sans',
	'Merriweather',
	'Nunito',
	'Ubuntu',
	'Playfair Display',
	'Noto Sans',
	'Mukta',
	'Rubik',
	'Work Sans',
	'Lora',
	'Fira Sans',
	'Nunito Sans',
	'Quicksand',
	'Karla',
	'Hind',
	'Barlow',
	'Titillium Web',
	'Oxygen',
	'Arimo',
	'Mulish',
	'Source Sans Pro',
	'Crimson Text',
	'Cabin',
	'Anton',
	'Bebas Neue',
	'Libre Franklin',
	'Libre Baskerville',
	'Abril Fatface',
	'Heebo',
	'Dancing Script',
	'Manrope',
	'Comfortaa',
	'Pacifico',
	'Exo 2',
	'Fjalla One',
	'Zilla Slab',
	'Yanone Kaffeesatz',
	'Inter',
	'DM Sans',
	'DM Serif Display',
	'IBM Plex Sans',
	'PT Serif',
	'Urbanist',
	'Archivo Black',
	'Bitter',
	'Cinzel',
	'Fredoka',
	'Lobster',
];

/**
 * Validate that a value is a safe CSS color (hex, rgb/rgba, hsl/hsla, or a
 * known named color keyword). Returns true only for values that cannot be used
 * for CSS injection.
 */
const SAFE_CSS_COLOR_RE =
	/^(#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(\s*,\s*(0|1|0?\.\d+))?\s*\)|hsla?\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%(\s*,\s*(0|1|0?\.\d+))?\s*\)|transparent|[a-z]+)$/i;

function isSafeCssColor(value: string): boolean {
	return SAFE_CSS_COLOR_RE.test(value.trim());
}

/**
 * Collect CSS rules for navbar links that use background-color.
 * mj-style must live in mj-head, so the Page block injects them centrally.
 */
function collectNavbarLinkStyles(
	block: IBlockData,
	idx: string,
	mode?: string,
	dataSource?: Record<string, any>,
): string {
	let css = '';

	if (block.type === BasicType.NAVBAR || block.type === AdvancedType.NAVBAR) {
		const links: Array<{ 'background-color'?: string }> =
			(block as any).data?.value?.links || [];
		const idxPrefix = `bay-nav-${idx
			.replace(/[.[\]]/g, '-')
			.replace(/-+/g, '-')
			.replace(/(^-|-$)/g, '')}`;
		links.forEach((link, linkIndex) => {
			const bg = resolvePreviewColor(link['background-color'], mode, dataSource);
			if (bg && bg !== '' && bg !== 'transparent' && isSafeCssColor(bg)) {
				css += `a.${idxPrefix}-${linkIndex} { background-color: ${bg} !important; display: inline-block !important; }\n`;
			}
		});
	}

	if (Array.isArray(block.children)) {
		block.children.forEach((child, index) => {
			css += collectNavbarLinkStyles(
				child,
				getChildIdx(idx, index),
				mode,
				dataSource,
			);
		});
	}

	return css;
}

/**
 * Extract Google Font names used anywhere in the email
 * Scans page defaults, block attributes, and recursively checks all children
 */
function extractGoogleFontsFromEmail(pageData: IPage): string[] {
	const foundFonts = new Set<string>();
	const googleFontsLookup = new Map(
		GOOGLE_FONTS.map(fontName => [fontName.toLowerCase(), fontName]),
	);

	// Helper to extract fonts from a font-family string
	function extractFontsFromString(fontFamily: string | undefined) {
		if (!fontFamily) return;
		const fonts = fontFamily
			.split(',')
			.map(f => f.trim().replace(/['"]/g, ''))
			.filter(Boolean);

		fonts.forEach(font => {
			const normalizedFont = font.toLowerCase();
			const canonicalFontName = googleFontsLookup.get(normalizedFont);
			if (canonicalFontName) {
				foundFonts.add(canonicalFontName);
			}
		});
	}

	function extractFontsFromHtmlContent(content: string | undefined) {
		if (!content || typeof content !== 'string') return;

		const fontFamilyMatches = content.matchAll(/font-family\s*:\s*([^;}{]+)/gi);

		for (const match of fontFamilyMatches) {
			const cssValue = match[1]?.trim();
			if (!cssValue) continue;
			extractFontsFromString(cssValue);
		}
	}

	// Recursively scan all blocks and their children
	function scanBlock(block: any) {
		// Check block attributes for font-family
		if (block?.attributes?.['font-family']) {
			extractFontsFromString(block.attributes['font-family']);
		}

		// Check block data value (for Page and some blocks like Text, Button, etc.)
		if (block?.data?.value?.['font-family']) {
			extractFontsFromString(block.data.value['font-family']);
		}

		// Check rich-text/html content where inline styles can set font-family
		Object.values(block?.data?.value || {}).forEach(value => {
			if (typeof value === 'string' && /font-family\s*:/i.test(value)) {
				extractFontsFromHtmlContent(value);
			}
		});

		// Recursively check children
		if (block?.children && Array.isArray(block.children)) {
			block.children.forEach(scanBlock);
		}
	}

	// Scan the entire page
	scanBlock(pageData);

	return Array.from(foundFonts);
}

function generateGoogleFontHref(fontName: string): string {
	return `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}&display=swap`;
}

function generateGoogleFontTags(fontNames: string[]): string {
	return fontNames
		.map(
			fontName =>
				`<mj-font name="${fontName}" href="${generateGoogleFontHref(fontName)}" />`,
		)
		.join('\n');
}

export type IPage = IBlockData<
	{
		'background-color'?: string;
		width: string;
	},
	{
		breakpoint?: string;
		headAttributes: string;
		fonts?: { name: string; href: string }[];
		headStyles?: {
			content?: string;
			inline?: 'inline';
		}[];
		extraHeadContent?: string;
		responsive?: boolean;
		'font-family': string;
		'font-size': string;
		'font-weight': string;
		'line-height': string;
		'text-color': string;
		'user-style'?: {
			content?: string;
			inline?: 'inline';
		};
		'content-background-color'?: string;
	}
>;

export const Page = createBlock<IPage>({
	get name() {
		return t('Page');
	},
	type: BasicType.PAGE,
	create: payload => {
		const defaultData: IPage = {
			type: BasicType.PAGE,
			data: {
				value: {
					breakpoint: '480px',
					headAttributes: '',
					'font-size': '14px',
					'font-weight': '400',
					'line-height': '1.7',
					headStyles: [],
					fonts: [],
					responsive: true,
					'font-family': 'Arial, sans-serif',
					'text-color': '#000000',
					'content-background-color': 'transparent',
				},
			},
			attributes: {
				'background-color': '#EDEDEE',
				width: '600px',
			},
			children: [Wrapper.create()],
		};
		return merge(defaultData, payload);
	},
	validParentType: [],
	render(params) {
		const { data } = params;
		const metaData = generaMjmlMetaData(data);
		const value = data.data.value;

		const breakpoint = value.breakpoint
			? `<mj-breakpoint width="${data.data.value.breakpoint}" />`
			: '';

		const navbarLinkCss = collectNavbarLinkStyles(
			data,
			getPageIdx(),
			params.mode,
			params.dataSource,
		);
		const navbarLinkStyleBlock = navbarLinkCss
			? `<mj-style inline="inline">${navbarLinkCss}</mj-style>`
			: '';

		// Keep a block's total rendered size within its defined width when a border or
		// padding is added. MJML emits images (and some blocks) without box-sizing, so a
		// border/padding gets added on top of the width (content-box) and overflows the
		// column. border-box folds the border/padding into the declared width instead.
		const boxSizingStyleBlock =
			'<mj-style>.email-block img { box-sizing: border-box; max-width: 100%; }</mj-style>';

		const nonResponsive = !value.responsive
			? `<mj-raw>
            <meta name="viewport" />
           </mj-raw>
		   <mj-style inline="inline">.mjml-body { width: ${
					data.attributes.width || '600px'
				}; margin: 0px auto; }</mj-style>`
			: '';
		const styles =
			value.headStyles
				?.map(
					style =>
						`<mj-style ${style.inline ? 'inline="inline"' : ''}>${
							style.content
						}</mj-style>`,
				)
				.join('\n') || '';

		const userStyle = value['user-style']
			? `<mj-style ${value['user-style'].inline ? 'inline="inline"' : ''}>${
					value['user-style'].content
				}</mj-style>`
			: '';

		const extraHeadContent = value.extraHeadContent
			? `<mj-raw>${value.extraHeadContent}</mj-raw>`
			: '';

		// Auto-detect and inject Google Fonts. MJML only applies an imported font
		// when the mj-font "name" matches the font-family used in the content.
		const googleFonts = extractGoogleFontsFromEmail(data);
		const googleFontsTag = generateGoogleFontTags(googleFonts);

		// Manual fonts from the "Import font" feature
		const manualFonts =
			value.fonts
				?.filter(Boolean)
				.map(item => `<mj-font name="${item.name}" href="${item.href}" />`)
				.join('\n') || '';

		return (
			<>
				{`
          <mjml>
          <mj-head>
              ${metaData}
              ${nonResponsive}
              ${boxSizingStyleBlock}
              ${styles}
              ${userStyle}
              ${navbarLinkStyleBlock}
              ${breakpoint}
              ${extraHeadContent}
              ${googleFontsTag}
              ${manualFonts}
            <mj-attributes>
              ${value.headAttributes}
              ${
								value['font-family']
									? `<mj-all font-family="${value['font-family'].replace(/"/gm, '')}" />`
									: ''
							}
              ${value['font-size'] ? `<mj-text font-size="${value['font-size']}" />` : ''}
              ${value['text-color'] ? `<mj-text color="${value['text-color']}" />` : ''}
        ${value['line-height'] ? `<mj-text line-height="${value['line-height']}" />` : ''}
        ${value['font-weight'] ? `<mj-text font-weight="${value['font-weight']}" />` : ''}
              ${
								value['content-background-color']
									? `<mj-wrapper background-color="${value['content-background-color']}" />
             <mj-section background-color="${value['content-background-color']}" />
            `
									: ''
							}

            </mj-attributes>
          </mj-head>
          <mj-body ${getAdapterAttributesString(params)}>`}

				{data.children.map((child, index) => (
					<BlockRenderer
						{...params}
						idx={getChildIdx(getPageIdx(), index)}
						key={index}
						data={child}
					/>
				))}

				{'</mj-body></mjml > '}
			</>
		);
	},
});
